import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./styles.module.css";
import {
  listCameras,
  createCamera,
  updateCamera,
  deleteCamera,
  regenerateLicenceKey,
} from "./data";

/* ---------- Defaults ---------- */
const emptyForm = {
  id: null,
  name: "",
  location_label: "",
  latitude: "",
  longitude: "",
  manufacturer: "",
  model: "",
  is_active: true,
  public_ulid: "",
  licence_key: null,
  last_seen_at: null,
  dateCreated: null,
  dateUpdated: null,
};

export default function CameraPanel({ open = false, onClose }) {
  const [isOpen, setIsOpen]       = useState(!!open);
  const [loading, setLoading]     = useState(false);
  const [list, setList]           = useState([]);
  const [msg, setMsg]             = useState(null);
  const [form, setForm]           = useState(emptyForm);
  const panelRef                  = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  useEffect(() => setIsOpen(!!open), [open]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [close]);

  // Backdrop click
  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
  }, [close]);

  // Open from settings panel event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("open-manage-cameras", handler);
    return () => window.removeEventListener("open-manage-cameras", handler);
  }, []);

  // Load list when opened
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setMsg(null);
      setLoading(true);
      try {
        const rows = await listCameras();
        setList(Array.isArray(rows) ? rows : []);
        if (rows?.length && !form.id) applyRowToForm(rows[0]);
      } catch (e) {
        setMsg(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyRowToForm = (r) => {
    setForm({
      id:             r.id,
      name:           r.name || "",
      location_label: r.location_label || "",
      latitude:       r.latitude ?? "",
      longitude:      r.longitude ?? "",
      manufacturer:   r.manufacturer || "",
      model:          r.model || "",
      is_active:      !!r.is_active,
      public_ulid:    r.public_ulid || "",
      licence_key:    r.licence_key || null,
      last_seen_at:   r.last_seen_at || null,
      dateCreated:    r.dateCreated || null,
      dateUpdated:    r.dateUpdated || null,
    });
  };

  const selectRow  = (r) => applyRowToForm(r);
  const newCamera  = () => setForm(emptyForm);

  const copyText = async (text, successMsg = "Copied") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setMsg(successMsg);
      setTimeout(() => setMsg(null), 1500);
    } catch {
      window.prompt(successMsg + " — press Ctrl/Cmd+C, then Enter:", text);
    }
  };

  const save = async () => {
    setMsg(null);
    const payload = {
      name:           String(form.name || "").trim(),
      location_label: form.location_label || null,
      manufacturer:   form.manufacturer || null,
      model:          form.model || null,
      is_active:      !!form.is_active,
    };

    const latStr = String(form.latitude ?? "").trim();
    const lonStr = String(form.longitude ?? "").trim();
    if (latStr !== "" && !Number.isNaN(Number(latStr))) payload.latitude = Number(latStr);
    if (lonStr !== "" && !Number.isNaN(Number(lonStr))) payload.longitude = Number(lonStr);

    setLoading(true);
    try {
      if (form.id) {
        await updateCamera(form.id, payload);
        const rows = await listCameras();
        setList(rows);
        const updated = rows.find((r) => r.id === form.id);
        if (updated) applyRowToForm(updated);
        setMsg("Saved.");
        setTimeout(() => setMsg(null), 1200);
      } else {
        const created = await createCamera(payload);
        const newId   = created?.data?.id ?? created?.id ?? null;
        const rows    = await listCameras();
        setList(rows);
        const row = (newId && rows.find((r) => r.id === newId)) || rows[rows.length - 1];
        if (row) applyRowToForm(row);
        // Auto-copy licence key on creation
        if (row?.licence_key) {
          await copyText(row.licence_key, "Camera created — licence key copied!");
        }
      }
    } catch (e) {
      setMsg(e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!form.id) return;
    if (!window.confirm("Delete this camera? The licence key will be immediately invalidated.")) return;
    setLoading(true);
    setMsg(null);
    try {
      await deleteCamera(form.id);
      const rows = await listCameras();
      setList(rows);
      newCamera();
      setMsg("Deleted.");
      setTimeout(() => setMsg(null), 1200);
    } catch (e) {
      setMsg(e.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    if (!form.id) return;
    if (!window.confirm(
      "Regenerate licence key? The current key will be immediately invalidated " +
      "and any Pi using it will stop until the new key is entered."
    )) return;
    setLoading(true);
    setMsg(null);
    try {
      const newKey = await regenerateLicenceKey(form.id);
      if (newKey) {
        setForm((f) => ({ ...f, licence_key: newKey }));
        const rows = await listCameras();
        setList(rows);
        await copyText(newKey, "New licence key generated and copied!");
      }
    } catch (e) {
      setMsg(e.message || "Failed to regenerate licence key");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        role="presentation"
        onClick={close}
        style={{
          position: "fixed", inset: 0,
          background: "color-mix(in oklab, black 35%, transparent)",
          zIndex: 1067,
        }}
      />

      {/* Dialog */}
      <div
        ref={panelRef}
        className={styles.filterPanel}
        role="dialog"
        aria-modal="true"
        aria-label="Cameras"
        style={{
          position: "fixed",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: Math.min(920, window.innerWidth - 48),
          maxWidth: "96vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          zIndex: 1068,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.18)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={styles.filterPanelHeader}
          style={{
            position: "sticky", top: 0,
            background: "#fff", zIndex: 1,
            borderBottom: "1px solid #eee",
          }}
        >
          <div className={styles.filterTitle}>Cameras</div>
          <button type="button" className={styles.xBtn} onClick={close} aria-label="Close">×</button>
        </div>

        {/* Scrollable content */}
        <div
          className={styles.filterSection}
          style={{ overflow: "auto", flex: "1 1 auto", padding: 16 }}
        >
          <div className={styles.muted} style={{ minHeight: 18 }}>
            {loading ? "Working…" : msg || ""}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "minmax(280px,280px) 1fr", gap: 16, alignItems: "start" }}>

            {/* LEFT: camera list */}
            <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div className={styles.fieldLabel}>All cameras</div>
                <button type="button" className={styles.applyBtn} onClick={newCamera}>New</button>
              </div>
              <div role="list">
                {list.length === 0 && (
                  <div className={styles.muted} style={{ padding: 8, textAlign: "center" }}>No cameras yet.</div>
                )}
                {list.map((r) => {
                  const selected = form.id === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => selectRow(r)}
                      role="listitem"
                      style={{
                        width: "100%", textAlign: "left",
                        border: "1px solid " + (selected ? "#c7d2fe" : "#eee"),
                        background: selected ? "#eef2ff" : "#fafafa",
                        borderRadius: 8, padding: 10, marginBottom: 8, cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.name || "Untitled camera"}
                        </div>
                        <div style={{
                          fontSize: 12, padding: "2px 8px", borderRadius: 999,
                          background: r.is_active ? "#e8f5e9" : "#f5f5f5",
                          border: "1px solid " + (r.is_active ? "#c8e6c9" : "#eee"),
                          color: r.is_active ? "#2e7d32" : "#666",
                        }}>
                          {r.is_active ? "Active" : "Inactive"}
                        </div>
                      </div>
                      <div style={{ color: "#555", marginTop: 4, fontSize: 13 }}>
                        {r.location_label || "—"}
                      </div>
                      {r.licence_key && (
                        <div style={{
                          marginTop: 6, fontFamily: "ui-monospace, monospace",
                          fontSize: 12, color: "#6366f1", letterSpacing: "0.05em",
                        }}>
                          {r.licence_key}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* RIGHT: editor */}
            <div style={{ border: "1px solid #eee", borderRadius: 8, padding: 12 }}>
              <div className={styles.fieldLabel} style={{ marginBottom: 8 }}>
                {form.id ? "Edit camera" : "Create camera"}
              </div>

              {/* Basic fields */}
              <div className={styles.inputWrap}>
                <label className={styles.fieldLabel}>Name</label>
                <input
                  className={styles.textInput}
                  value={form.name}
                  onChange={(e) => onField("name", e.target.value)}
                  placeholder="Descriptive name"
                />
              </div>
              <div className={styles.inputWrap}>
                <label className={styles.fieldLabel}>Location</label>
                <input
                  className={styles.textInput}
                  value={form.location_label}
                  onChange={(e) => onField("location_label", e.target.value)}
                  placeholder="e.g. South Gate"
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className={styles.inputWrap}>
                  <label className={styles.fieldLabel}>Latitude</label>
                  <input
                    className={styles.textInput}
                    value={form.latitude}
                    onChange={(e) => onField("latitude", e.target.value)}
                    placeholder="52.4862"
                  />
                </div>
                <div className={styles.inputWrap}>
                  <label className={styles.fieldLabel}>Longitude</label>
                  <input
                    className={styles.textInput}
                    value={form.longitude}
                    onChange={(e) => onField("longitude", e.target.value)}
                    placeholder="-1.8904"
                  />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className={styles.inputWrap}>
                  <label className={styles.fieldLabel}>Manufacturer</label>
                  <input
                    className={styles.textInput}
                    value={form.manufacturer}
                    onChange={(e) => onField("manufacturer", e.target.value)}
                    placeholder="Vendor"
                  />
                </div>
                <div className={styles.inputWrap}>
                  <label className={styles.fieldLabel}>Model</label>
                  <input
                    className={styles.textInput}
                    value={form.model}
                    onChange={(e) => onField("model", e.target.value)}
                    placeholder="Model"
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div className={styles.inputWrap} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                <input
                  id="is-active"
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => onField("is_active", e.target.checked)}
                />
                <label htmlFor="is-active" className={styles.fieldLabel} style={{ margin: 0 }}>
                  Active
                </label>
              </div>

              {/* Licence key section */}
              {form.id && (
                <div style={{
                  marginTop: 12, padding: 12,
                  border: "1px solid #e0e7ff",
                  borderRadius: 8, background: "#f5f3ff",
                }}>
                  <div className={styles.fieldLabel} style={{ marginBottom: 8, color: "#4338ca" }}>
                    Edge Device Licence Key
                  </div>
                  <div style={{ marginBottom: 8, fontSize: 13, color: "#555" }}>
                    Enter this key into the edge device web interface to link it to this camera.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
                    <input
                      className={styles.textInput}
                      value={form.licence_key || ""}
                      readOnly
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        letterSpacing: "0.1em",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#4338ca",
                        background: "#ede9fe",
                        border: "1px solid #c4b5fd",
                      }}
                    />
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={() => copyText(form.licence_key, "Licence key copied!")}
                      disabled={!form.licence_key}
                    >
                      Copy
                    </button>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                    {form.last_seen_at
                      ? `Last seen: ${form.last_seen_at}`
                      : "No edge device has connected yet"}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className={styles.filterActions} style={{ marginTop: 16 }}>
                {form.id ? (
                  <>
                    <button type="button" className={styles.resetBtn} onClick={remove}>
                      Delete
                    </button>
                    <div className={styles.flexSpacer} />
                    <button type="button" className={styles.secondaryBtn} onClick={regenerate}>
                      Regenerate Key
                    </button>
                    <button type="button" className={styles.applyBtn} onClick={save}>
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.flexSpacer} />
                    <button type="button" className={styles.applyBtn} onClick={save}>
                      Create
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}